import * as React from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import {
  PanelMarker,
  BloodworkAnalysis,
  BloodworkNote,
  FurtherConsiderations,
  Section,
} from '../models';
import { FurtherConsiderationsSection } from './bloodworkz/further-considerations-section';
import { ViscosityInsights } from './bloodworkz/viscosity-insights';
import { AboutSection } from './bloodworkz/about-section';
import { ReferenceRangeAnalysisContainer } from '../containers/bloodwork-ref-range-analysis-container';
import { BloodworkEntryStore } from '../stores/bloodwork-entry-store';
import { BloodworkNotes } from './bloodworkz/bloodwork-notes';
import { SupplementsEngine } from '../services/supplements-engine';
import { supplementsVisible } from '../utils/supplementsVisibility';

type Props = {
  section: Section;
  panelMarkers: PanelMarker[];
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  bloodworkEntryStore: BloodworkEntryStore;
  infoLink: string;
  enabledSupplementBrands?: string[];
};

type State = {
  bloodworkNote: BloodworkNote;
};

export class TabsComponent extends React.Component<Props, {}> {
  state: State = {
    bloodworkNote: this.initBloodworkNote(
      this.props.section,
      this.props.bloodworkAnalysis,
    ),
  };

  componentWillReceiveProps(nextProps) {
    const { section, bloodworkAnalysis } = nextProps;
    this.setState({
      bloodworkNote: this.initBloodworkNote(section, bloodworkAnalysis),
    });
  }

  initBloodworkNote(
    section: string,
    bloodworkAnalysis: BloodworkAnalysis,
  ): BloodworkNote {
    if (
      bloodworkAnalysis &&
      bloodworkAnalysis.bloodwork &&
      bloodworkAnalysis.bloodwork.bloodworkNotes
    ) {
      let bloodworkNote =
        bloodworkAnalysis.bloodwork.bloodworkNotes.findNote(section);
      if (!bloodworkNote) {
        bloodworkNote = bloodworkAnalysis.bloodwork.bloodworkNotes.addNote(
          section,
          '',
        );
      }
      return new BloodworkNote(bloodworkNote);
    }
    return new BloodworkNote();
  }

  handleInputChange = (event: any) => {
    const { section } = this.props;
    const value = event.target.value;
    this.setState({
      bloodworkNote: new BloodworkNote({ section, text: value }),
    });
  };

  saveOrUpdate = (event: any) => {
    const { section, bloodworkAnalysis, bloodworkEntryStore } = this.props;
    event.preventDefault();
    const bloodworkNote =
      bloodworkAnalysis.bloodwork.bloodworkNotes.findNote(section);
    bloodworkNote.text = this.state.bloodworkNote.text;
    bloodworkEntryStore.saveBloodwork(bloodworkAnalysis.bloodwork).catch((err) => {
      console.error('Failed to save bloodwork note', err);
      alert('Failed to save your note. Please try again.');
    });
  };

  showInsightsTab(): boolean {
    const { section, bloodworkAnalysis } = this.props;
    if (section === Section.PLATELETS) {
      // Blood Clotting Function shows a Viscosity insight (the calculation)
      // only when viscosity could be calculated (HCT + total protein present).
      return ViscosityInsights.shouldRender(bloodworkAnalysis);
    }
    return [Section.RBC, Section.WBC, Section.GLUCOSE].includes(section);
  }

  showPossibleRootCausesTab(): boolean {
    const { section } = this.props;
    return section !== Section.RBC;
  }

  showOtherTabs(): boolean {
    const { section } = this.props;
    return section === Section.RBC;
  }

  showFurtherConsiderationsTab(): boolean {
    const { section } = this.props;
    return [Section.RBC, Section.WBC, Section.GLUCOSE].includes(section);
  }

  showSupplementsTab(recommendations: any[]): boolean {
    const { section, furtherConsiderations } = this.props;
    const categories =
      this.props.bloodworkAnalysis.possibleRootCauseCategoriesFor(section);

    return (
      // Everywhere but prod — see supplementsVisible() for the fail-closed
      // env check shared by all supplement features.
      supplementsVisible() &&
      (section === Section.RBC ||
        section === Section.WBC ||
        section === Section.GLUCOSE) &&
      categories.some(
        category =>
          furtherConsiderations.findConsiderations(
            category.rootCauseCategory,
            section,
            'Supplements',
            this.props.enabledSupplementBrands,
          ).length,
      )
    );
  }

  showReferencesTab(): boolean {
    const { section, furtherConsiderations } = this.props;
    const categories =
      this.props.bloodworkAnalysis.possibleRootCauseCategoriesFor(section);

    return (
      (section === Section.GLUCOSE ||
        section === Section.WBC ||
        section === Section.PLATELETS ||
        section === Section.LIVER_BILIARY) &&
      categories.some(
        category =>
          furtherConsiderations.findConsiderations(
            category.rootCauseCategory,
            section,
            'References',
          ).length,
      )
    );
  }

  refRangeTitle(): string {
    const { section } = this.props;
    if (section === Section.WBC) {
      return 'Immune Activity Results';
    } else if (section === Section.PLATELETS) {
      return 'Blood Clotting Results';
    }
    else if (section === Section.PROTEINS) {
      return 'Protein Results';
    }
    else if (section === Section.NUTRIENTS) {
      return 'Vitamin & Mineral Results';
    }
    else if (section === Section.SEX_HORMONE) {
      return 'Hormone Results';
    }
    else if (section === Section.INFLAMMATION_OXIDATIVE_STRESS) {
      return 'Inflammation & Oxidative Stress Results';
    }
    else if (section === Section.LIPIDS) {
      return 'Lipid Results';
    }
    else if (section === Section.LIVER_BILIARY) {
      return 'Liver Results';
    }
    else if (section === Section.ELECTROLYTES) {
      return 'Electrolyte Results';
    }
    else if (section === Section.STRESS_ADRENAL) {
      return 'Stress & Adrenal Results';
    }
    else if (section === Section.GLUCOSE) {
      return 'Glucose Regulation & Metabolic Function Results';
    }
    return `${section} Results`;
  }

  render() {
    const {
      section,
      bloodworkAnalysis,
      furtherConsiderations,
      panelMarkers,
      bloodworkEntryStore,
      infoLink,
      enabledSupplementBrands,
    } = this.props;

    const supplementRecommendations = SupplementsEngine.getRecommendations(
      section,
      bloodworkAnalysis,
      enabledSupplementBrands || [],
    );

    return (
      <Tabs defaultActiveKey={1} id="uncontrolled-tab-example">
        <Tab eventKey={1} title="Reference Range">
          <br />
          <ReferenceRangeAnalysisContainer
            title={this.refRangeTitle()}
            section={section}
            bloodwork={bloodworkAnalysis.bloodwork}
            previousBloodwork={bloodworkAnalysis.previousBloodwork}
            infoLink={infoLink}
            panelMarkers={panelMarkers}
          />
        </Tab>

        {this.showInsightsTab() ? (
          <Tab eventKey={2} title="Insights">
            <br />
            {section === Section.PLATELETS ? (
              <ViscosityInsights bloodworkAnalysis={bloodworkAnalysis} />
            ) : (
              <FurtherConsiderationsSection
                section={section}
                tab="Interpretation"
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
              />
            )}
          </Tab>
        ) : null}

        {this.showPossibleRootCausesTab() ? (
          <Tab eventKey={3} title="Possible Root Causes">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="Causes"
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        {this.showOtherTabs() ? (
          <Tab eventKey={4} title="Additional Testing">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="Testing"
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        {this.showFurtherConsiderationsTab() ? (
          <Tab eventKey={5} title="Further Considerations">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="Considerations"
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        {this.showOtherTabs() ? (
          <Tab eventKey={6} title="Signs and Symptoms">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="Symptoms"
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        {this.showOtherTabs() ? (
          <Tab eventKey={7} title="Diet">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="Diet"
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        {this.showOtherTabs() ? (
          <Tab eventKey={8} title="Lifestyle">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="Lifestyle"
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        {this.showSupplementsTab(supplementRecommendations) ? (
          <Tab eventKey={9} title="Supplements">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="Supplements"
              brands={enabledSupplementBrands}
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        <Tab eventKey={12} title="About">
          <br />
          <AboutSection
            section={section}
            panelMarkers={panelMarkers}
            furtherConsiderations={furtherConsiderations}
          />
        </Tab>

        {this.showReferencesTab() ? (
          <Tab eventKey={11} title="References">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="References"
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        <Tab eventKey={10} title="Notes">
          <br />
          <BloodworkNotes
            section={section}
            title="Add your own notes related to this analysis here. Click Save when finished."
            bloodwork={bloodworkAnalysis.bloodwork}
            bloodworkEntryStore={bloodworkEntryStore}
            // These props power the "gather from tabs" digest under Save.
            bloodworkAnalysis={bloodworkAnalysis}
            furtherConsiderations={furtherConsiderations}
            panelMarkers={panelMarkers}
          />
        </Tab>
      </Tabs>
    );
  }
}
